import { useEffect, useRef, type ReactNode } from 'react';
import styles from './Sheet.module.css';

interface SheetProps {
  open: boolean;
  label: string;
  onClose(): void;
  /** Escape runs this when given (a cancel that reverts), otherwise it closes like the scrim. */
  onCancel?: () => void;
  /** Extra class on the panel, such as a size scale for its controls. */
  className?: string;
  children: ReactNode;
}

/** Bottom sheet on the phone, centered dialog on a desktop. The scrim closes it; Escape cancels or closes. */
export function Sheet({ open, label, onClose, onCancel, className, children }: SheetProps) {
  const pressedScrim = useRef(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') (onCancel ?? onClose)();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onCancel]);

  if (!open) return null;
  return (
    <div
      className={styles.overlay}
      // Close on the release, not the press: the overlay is still there to swallow the tap, so
      // a card under the finger is not opened by the same touch that closed the sheet.
      onPointerDown={(e) => {
        pressedScrim.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedScrim.current && e.target === e.currentTarget) onClose();
        pressedScrim.current = false;
      }}
    >
      <div className={`${styles.sheet}${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={label}>
        <div className={styles.grabber} aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}
